<template>
  <b-modal
    v-model="modalShow"
    size="xl"
    centered
    :title-html="localMode == 'initiate_packing' ? 'Initiate Packing' : 'Complete Packing'"
    @hidden="$emit('modal-closed')"
  >
    <b-alert
      variant="danger"
      :show="errorMessage ? true : false"
    >
      <div class="alert-body">
        <p>{{ errorMessage }}</p>
      </div>
    </b-alert>

    <!-- Initiate Packing Form -->
    <div v-show="localMode == 'initiate_packing'">
      <b-form @submit.prevent="initiatePackingHandler">
        <b-form-group
          label="User"
          style="width: 40%;"
        >
          <custom-v-select
            ref="user"
            v-model="selectedUserId"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="filteredUsers"
            label="name"
            placeholder="Type to search user..."
            :reduce="(item) => item.id"
            :filterable="true"
            @search="handleUserSearch"
          />
        </b-form-group>

        <b-button
          type="submit"
          variant="primary"
          :disabled="!selectedUserId || submitting"
        >
          Initiate Packing
          <b-spinner
            v-if="submitting"
            small
            label="Small Spinner"
          />
        </b-button>
      </b-form>
    </div>

    <!-- Complete Packing Form -->
    <div v-show="localMode == 'complete_packing'">
      <b-table
        v-if="!loading && reportData && reportData.length > 0"
        :items="reportData"
        :fields="reportTableFields"
        striped
        hover
        show-empty
        class="compact-table1"
        caption-top
      >
        <template #table-caption>
          <h5 class="mb-0">
            Rack Groups
          </h5>
        </template>

        <!-- Dynamic slots for each rack group -->
        <template
          v-for="group in rackGroupOptions"
          v-slot:[`cell(rack_group_${group})`]="{ item }"
        >
          <rack-group-items
            :key="`rack-group-${group}-${item.id}`"
            :operation-users="operationUsers"
            :rg-reports="item.rg_reports ? item.rg_reports.filter(report => report.rack_group_no === group) : []"
          />
        </template>
      </b-table>

      <!-- Bills Table -->
      <b-table
        v-if="!loading && bills.length > 0"
        :items="bills"
        :fields="tableFields"
        striped
        hover
        show-empty
        class="compact-table1"
        no-local-sorting
        caption-top
        :tbody-tr-class="applyRowClass"
      >
        <template #table-caption>
          <h5 class="mb-0">
            Bills
          </h5>
        </template>

        <template #cell(bill_no)="{item}">
          <span class="d-flex align-items-center">
            {{ item.bill_no }}
            <span
              v-if="item.is_modified"
              class="is-modified-bill"
            />
            <span
              v-if="item.is_urgent"
              class="is-urgent-bill"
            />
            <feather-icon
              v-if="item.bill_delivery_info && item.bill_delivery_info.delivery_otp && item.bill_delivery_info.delivery_otp.verified"
              v-b-tooltip.hover="{ boundary: 'window' }"
              title="OTP Verified"
              icon="CheckIcon"
              size="16"
              class="text-primary mx-1"
            />
          </span>
        </template>

        <template #cell(date)="data">
          {{ data.item.bill_delivery_info && data.item.bill_delivery_info.generated_at ? formatedDate(data.item.bill_delivery_info.generated_at ,'DD-MM-YYYY LT') :'-' }}
        </template>

        <template #cell(status)="data">
          <Status
            :delivery-status="data.item.bill_delivery_info"
            :packing-status="data.item.rack_group_status"
          />
        </template>

        <template #cell(user)="data">
          <span>{{ lastUser(data).user || '-' }}</span>
        </template>

        <template #cell(user_time)="data">
          <span>{{ lastUser(data).time && formatedDate(lastUser(data).time ,'DD-MM-YYYY LT') || '-' }}</span>
        </template>
      </b-table>

      <!-- Loading state -->
      <div
        v-if="loading"
        class="text-center py-4"
      >
        <b-spinner label="Loading..." />
        <p class="mt-2">
          Loading data...
        </p>
      </div>

      <!-- No data state -->
      <div
        v-if="!loading && (!reportData || reportData.length === 0) && bills.length === 0"
        class="text-center py-4"
      >
        <p class="text-muted">
          No data available
        </p>
      </div>

      <b-form-group v-if="isAutoPrintAllowed">
        <b-form-checkbox
          v-model="isLocalPrintAllowed"
          :checked="isAutoPrintAllowed"
        >
          <span class="text-danger">Print bill ({{ totalCopies }} copies) after submitting</span>
        </b-form-checkbox>
      </b-form-group>

      <b-form-group v-if="packingNotesExists">
        <b-form-checkbox
          v-model="isNoteChecked"
        >
          <span class="text-danger font-weight-bold">Taking action on packing note</span>
        </b-form-checkbox>
      </b-form-group>

      <b-button
        type="submit"
        variant="primary"
        :disabled="submitting || (packingNotesExists && !isNoteChecked)"
        @click="completePackingHandler"
      >
        Complete Packing
        <b-spinner
          v-if="submitting"
          small
          label="Small Spinner"
        />
      </b-button>

    </div>

    <template #modal-footer="{ cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
    </template>
  </b-modal>

</template>

<script>
import {
  BButton, BSpinner, BForm, BFormGroup, BAlert,
  BFormCheckbox,
  BTable,
} from 'bootstrap-vue'
import axios from 'axios'
import getParsedQueryString from '@/utils/headerQueryParser'
import downloadFileMixin from '@/mixins/downloadFileMixin'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import Status from '@/components/PartyLedger/Status.vue'
import RackGroupItems from './RackGroupItems.vue'
import { printPDFs } from './pdfPrintUtils'

export default {
  name: 'InitiatePackingModal',
  components: {
    BButton,
    BSpinner,
    BFormGroup,
    BForm,
    CustomVSelect,
    BAlert,
    BFormCheckbox,
    BTable,
    Status,
    RackGroupItems,
  },
  mixins: [downloadFileMixin, dateMixin, formatMixin],
  props: {
    operationUsers: {
      type: Array,
      required: true,
    },
    billRgStatusIds: {
      type: Array,
      required: true,
    },
    isAutoPrintAllowed: {
      type: Boolean,
      default: false,
    },
    partyIds: {
      type: Array,
      required: true,
    },
    rackGroupOptions: {
      type: Array,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      modalShow: true,
      loading: false,
      submitting: false,
      selectedUserId: null,
      errorMessage: null,
      reportData: [],
      isLocalPrintAllowed: false,
      isNoteChecked: false,
      localMode: null,
      localPartyIds: [],
      pdfUrls: [],
      bills: [],
      searchQuery: '',
      filteredUsers: [],
      tableFields: [
        { key: 'bill_no', label: 'Bill No.', sortable: true },
        { key: 'party__identifier', label: 'Buyer Name', sortable: true },
        {
          key: 'date', label: 'Date', sortable: true, thStyle: { minWidth: '180px' },
        },
        { key: 'status', label: 'Status' },
        { key: 'user', label: 'User' },
        { key: 'user_time', label: 'Last Updated', thStyle: { minWidth: '180px' } },
      ],
    }
  },
  computed: {
    totalCopies() {
      return this.pdfUrls.map(item => item.prints || 1).reduce((total, prints) => total + prints, 0)
    },
    packingNotesExists() {
      return this.reportData.map(item => item.packing_note).some(value => !!value)
    },
    reportTableFields() {
      const baseFields = [
        { key: 'invoice__bill_no', label: 'Bill No', sortable: true },
      ]

      // Add dynamic fields for each rack group
      const rackGroupFields = this.rackGroupOptions.map(group => ({
        key: `rack_group_${group}`,
        label: `# ${group}`,
      }))

      const otherFields = [
        { key: 'packing_note', label: 'Packing Note', sortable: true },
        { key: 'packing_initiated_by_actual', label: 'Packing Initiated By', sortable: true },
      ]

      return [...baseFields, ...rackGroupFields, ...otherFields]
    },
  },
  mounted() {
    this.isLocalPrintAllowed = this.isAutoPrintAllowed
    this.localMode = this.mode
    this.localPartyIds = this.partyIds

    if (this.localMode === 'complete_packing') {
      this.fetchBillRGStatus()
    }

    if (this.localMode === 'initiate_packing') {
      setTimeout(() => {
        const input = this.$refs.user.$el.querySelector('input')
        if (input) {
          input.focus()
        }
      }, 150)
    }
  },
  methods: {
    handleUserSearch(search) {
      this.searchQuery = search
      if (!search || search.trim() === '') {
        // Keep selected user visible even when search is empty
        if (this.selectedUserId) {
          const selectedUser = this.operationUsers.find(user => user.id === this.selectedUserId)
          this.filteredUsers = selectedUser ? [selectedUser] : []
        } else {
          this.filteredUsers = []
        }
      } else {
        this.filteredUsers = this.operationUsers
      }
    },

    async fetchBillRGStatus() {
      try {
        this.loading = true
        const fromDate = new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

        const rgParams = {
          ids: [...this.billRgStatusIds],
          page: 1,
          per_page: 100,
          sort_by: 'invoice__bill_no',
          sort_desc: true,
        }
        const res = await axios.get(`/operations/bill_rack_group_status/?${getParsedQueryString(rgParams)}`)

        const billRGStatusRecords = res.data.results

        this.reportData = billRGStatusRecords
        this.pdfUrls = billRGStatusRecords.map(report => {
          const bucketNumbers = report.rg_reports.map(item => item.pickup_bucket_numbers).flat()
          const uniqueNumber = [...new Set(bucketNumbers)]
          const basketNUmberString = uniqueNumber.join(', ')

          return {
            url: report.bill_attachments[0].file,
            prints: report.no_of_prints || 1,
            bucketNumbers: basketNUmberString,
            packingUser: report.packing_initiated_by_actual,
          }
        })

        const params2 = {
          party_ids: [...this.localPartyIds],
          from_date: fromDate,
          page: 1,
          per_page: 100,
        }
        const res2 = await axios.get(`/operations/packing_related_bills?${getParsedQueryString(params2)}`)

        const bills = res2.data.results

        // add tableIndex in each item
        const billsWithIndex = bills.map((item, index) => ({ tableIndex: index, ...item }))

        this.bills = billsWithIndex
      } catch (error) {
        this.errorMessage = error?.response?.data?.detail || 'Failed to fetch details'
      } finally {
        this.loading = false
      }
    },

    initiatePackingHandler() {
      if (!this.selectedUserId) {
        this.errorMessage = 'Please select a user'
        return
      }

      this.submitting = true
      this.errorMessage = null

      axios.post('/operations/initiate-packing', {
        bill_rg_status_ids: this.billRgStatusIds,
        operation_user_id: this.selectedUserId,
      })
        .then(res => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.data.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })

          this.$emit('updated')

          // Trigger mode update
          this.localMode = 'complete_packing'

          // Fetch Bill RG Status Records, Required for complete_packing mode
          this.fetchBillRGStatus()
        })
        .catch(error => {
          this.errorMessage = error?.response?.data?.detail || 'Failed to update status!'
        })
        .finally(() => {
          this.submitting = false
        })
    },

    lastUser(data) {
      const { item } = data

      if (item?.bill_delivery_info?.delivered_by_fullname) {
        return {
          user: item.bill_delivery_info.delivered_by_fullname,
          time: item.bill_delivery_info.delivered_at,
        }
      }

      if (item?.bill_delivery_info?.picked_by_fullname) {
        return {
          user: item.bill_delivery_info.picked_by_fullname,
          time: item.bill_delivery_info.picked_at,
        }
      }

      if (item?.rack_group_status?.packing_initiated_by_actual) {
        return {
          user: item.rack_group_status.packing_initiated_by_actual,
          time: item.rack_group_status.packing_initiated_at,
        }
      }

      if (item?.bill_delivery_info?.generated_by) {
        return {
          user: item.bill_delivery_info.generated_by,
          time: item.bill_delivery_info.generated_at,
        }
      }

      return { user: '', time: '' }
    },

    applyRowClass(item) {
      const classes = []

      // Highlight selected rows
      if (this.billRgStatusIds.includes(item?.rack_group_status?.id)) {
        classes.push('selected-row')
      }

      // Add party separator
      const index = item.tableIndex
      const currentParty = item.party
      const nextItem = this.bills[index + 1]
      const nextParty = nextItem ? nextItem.party : null

      if (!nextItem || currentParty !== nextParty) {
        classes.push('party-separator')
      }

      return classes.join(' ')
    },

    completePackingHandler() {
      this.submitting = true
      axios.post('/operations/mark-packing-completed', {
        bill_rg_status_ids: this.billRgStatusIds,
      })
        .then(res => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.data.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })

          // Trigger bill print
          if (this.isAutoPrintAllowed && this.isLocalPrintAllowed) {
            try {
              printPDFs(this.pdfUrls)
            } catch (error) {
              this.errorMessage = `Failed to print PDF: ${error.message}`
            }
          }

          this.$emit('updated')
          this.modalShow = false
        }).catch(error => {
          this.errorMessage = error?.response?.data?.detail || 'Failed to update status!'
          this.submitting = false
        })
    },
  },
}
</script>

<style scoped>
.selected-row {
  background-color: #e3f2fd !important;
}

.party-separator {
  border-bottom: 2px solid #dee2e6 !important;
}

.is-modified-bill,
.is-urgent-bill {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-left: 5px;
}

.is-modified-bill {
  background-color: #ffc107;
}

.is-urgent-bill {
  background-color: #dc3545;
}

.compact-table1 {
  font-size: 0.875rem;
}

.compact-table1 th,
.compact-table1 td {
  padding: 0.5rem;
  vertical-align: middle;
}
</style>
